import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuContent,
} from "@/components/ui/navigation-menu";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import SponsoredSlider from "@/components/SponsoredSlider";
import "./navbar.css";

interface NavItem {
  name: string;
  path?: string;
  subItems?: NavItem[];
  external?: boolean;
}

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const { pathname } = useLocation();

  // console.log(pathname);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems: NavItem[] = [
    {
      name: "Pmn",
      subItems: [
        {
          name: "Le Coordinateur",
          path: "/cordonnateur",
          subItems: [
            { name: "Décret", path: "/regulations" },
            { name: "4F", path: "/four" },
          ],
        },
        { name: "Missions", path: "/missions" },
        { name: "Objectifs du PMN", path: "/objectifs" },
        {
          name: "Organisation",
          subItems: [
            { name: "Comité de Pilotage", path: "/commite-de-pilotage" },
            { name: "Unité de Gestion", path: "/unite-de-gestion" },
            { name: "Organigramme", path: "/organigramme" },
          ],
        },
      ],
    },
    {
      name: "Activités",
      subItems: [
        { name: "Réalisations", path: "/realisations" },
        { name: "Actualités", path: "/actualites" },
      ],
    },
    {
      name: "Documentation",
      subItems: [
        { name: "Le Projet", path: "/projet" },
        { name: "Textes réglementaires", path: "/regulations" },
        { name: "Rapports et publications", path: "/rapport-et-publications" },
      ],
    },
    {
      name: "Domains",
      subItems: [
        { name: "Filières artisanales", path: "/filieres-artisanales" },
        { name: "Bénéficiaires du pmn", path: "/benificiaires-du-pmn" },
        {
          name: "Fiche de recensement",
          path: "https://pmn.vercel.app/artisans/inscription",
          external: true,
        },
        {
          name: "Portail Marchés publics",
          path: "https://pmn.vercel.app/marchespublics",
          external: true,
        },
      ],
    },
    {
      name: "Liens Utiles",
      subItems: [
        {
          name: "Site du Gouvernement",
          path: "https://primature.sn/",
          external: true,
        },
        {
          name: "Gendarmerie",
          path: "https://gendarmerie.sn/",
          external: true,
        },
        {
          name: "Sapeurs-pompiers",
          path: "https://www.sapeurspompiers.gouv.sn/",
          external: true,
        },
        {
          name: "L'actualité du senegal",
          subItems: [
            { name: "RTS", path: "https://www.rts.sn/", external: true },
            { name: "APS", path: "https://aps.sn/", external: true },
            { name: "Soleil", path: "https://lequotidien.sn/", external: true },
          ],
        },
      ],
    },
  ];

  const simpleNavItems: NavItem[] = [{ name: "Accueil", path: "/" }];

  const renderSubItems = (items: NavItem[]) => {
    return items.map((subItem) => (
      <div key={subItem.name}>
        {subItem.subItems ? (
          <div className="group text-sm">
            {subItem.path ? (
              <a
                href={subItem.path}
                className="font-medium text-[#1E3D1A] group-hover:text-[#069861]"
              >
                {subItem.name}
              </a>
            ) : (
              <div className="font-medium text-[#1E3D1A] group-hover:text-[#069861]">
                {subItem.name}
              </div>
            )}
            <div className="mt-2 space-y-2 ml-4">
              {renderSubItems(subItem.subItems)}
            </div>
          </div>
        ) : (
          <a
            href={subItem.path || "#"}
            target={subItem.external ? "_blank" : undefined}
            className={`text-gray-600 text-sm inline-flex hover:text-[#069861] ${
              pathname === subItem?.path
                ? "bg-[#069861] p-2 rounded text-white hover:text-white"
                : ""
            }`}
          >
            {subItem.name}
          </a>
        )}
      </div>
    ));
  };

  const renderMobileSubItems = (items: NavItem[]) => {
    return items.map((subItem) => (
      <div key={subItem.name} className="pl-4 text-sm">
        {subItem.subItems ? (
          <div className="space-y-2">
            {subItem.path ? (
              <a
                href={subItem.path}
                className="font-medium text-[#1E3D1A] block py-2 hover:text-[#069861]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {subItem.name}
              </a>
            ) : (
              <div className="font-medium text-[#1E3D1A] py-2">
                {subItem.name}
              </div>
            )}
            <div className="space-y-2 pl-4">
              {renderMobileSubItems(subItem.subItems)}
            </div>
          </div>
        ) : (
          <a
            href={subItem.path || "#"}
            target={subItem.external ? "_blank" : undefined}
            className={`block py-2 text-gray-600 hover:text-[#069861] ${
              pathname === subItem?.path ? "text-[#069861]" : ""
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {subItem.name}
          </a>
        )}
      </div>
    ));
  };

  return (
    <div
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? "shadow-lg" : ""
      }`}
    >
      {/* Sponsored Slider */}
      <div className="bg-gradient-to-r from-orange-50 to-white py-2 px-4">
        <SponsoredSlider />
      </div>

      {/* Main Navbar */}
      <nav
        className={`bg-white transition-all duration-300 border-b border-gray-200 ${
          isScrolled ? "py-2" : "py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center">
              <a href="/" className="flex-shrink-0 flex items-center">
                <img
                  src="/logo/logo-navbare.png"
                  alt="PMN Logo"
                  className="w-12 h-12 object-cover object-center"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              {simpleNavItems.map((item) => (
                <a
                  key={item.name}
                  href={item.path || "#"}
                  className={`text-gray-600 hover:text-[#069861] ${
                    pathname === item.path ? "text-[#069861]" : ""
                  }`}
                >
                  {item.name}
                </a>
              ))}

              <NavigationMenu viewport={false}>
                <NavigationMenuList className="space-x-1">
                  {navItems.map((item) => (
                    <NavigationMenuItem key={item.name}>
                      <NavigationMenuTrigger
                        className={`bg-transparent text-sm hover:bg-[#069861]/10 text-gray-700 hover:text-[#069861] ${
                          pathname === item?.path
                            ? "bg-[#069861] text-white"
                            : ""
                        }`}
                      >
                        {item.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent className="!left-0 !right-auto !transform-none">
                        <div className="grid w-full min-w-[280px] gap-3 p-4">
                          {renderSubItems(item.subItems || [])}
                        </div>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ))}
                </NavigationMenuList>
              </NavigationMenu>
              <a href="/gallery">
                <Button
                  variant="ghost"
                  className={`bg-transparent text-sm hover:bg-[#069861]/10 text-gray-700 hover:text-[#069861]`}
                >
                  Mediatheque
                </Button>
              </a>
              <a href="/contact">
                <Button
                  variant="ghost"
                  className={`bg-transparent text-sm hover:bg-[#069861]/10 text-gray-700 hover:text-[#069861] `}
                >
                  Contact
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <button className="bg-[#1E3D1A] p-2 rounded text-white hover:text-[#069861]">
                    <Menu size={24} />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="w-[300px] sm:w-[400px] bg-white h-[100vh] overflow-y-auto"
                >
                  <SheetHeader className="border-b border-gray-200">
                    <SheetTitle className="text-left text-[#1E3D1A]">
                      Menu
                    </SheetTitle>
                  </SheetHeader>
                  <div className="px-4 space-y-2">
                    {simpleNavItems.map((item) => (
                      <a
                        key={item.name}
                        href={item.path || "#"}
                        className={`block text-sm p-2 rounded text-gray-700 hover:text-[#069861] `}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </a>
                    ))}
                    {navItems.map((item) => (
                      <div key={item.name} className="space-y-2">
                        <div className="font-medium text-[#1E3D1A] text-base p-2">
                          {item.name}
                        </div>
                        <div className="space-y-2">
                          {renderMobileSubItems(item.subItems || [])}
                        </div>
                      </div>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
